/**
 * Centralized Authentication Utilities
 * Provides authentication functions for the homepage and shared services
 */
import { supabase } from './supabase';
import { getOrCreateUser } from './supabase';

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated() {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
}

/**
 * Get current user
 * @returns {Promise<Object|null>}
 */
export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

/**
 * Get current user ID
 * @returns {Promise<string|null>}
 */
export async function getUserId() {
  const user = await getCurrentUser();
  return user?.id || null;
}

/**
 * Login with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>}
 */
export async function login(email, password) {
  console.log('[AUTH] Attempting login for:', email);
  
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) {
      console.error('[AUTH] Login error:', error);
      throw error;
    }
    
    console.log('[AUTH] Login successful, user ID:', data.user?.id);
    
    // Ensure user exists in users table
    if (data.user) {
      try {
        await getOrCreateUser(data.user.id);
        console.log('[AUTH] User record verified/created');
      } catch (userError) {
        console.warn('[AUTH] Error creating/getting user (non-blocking):', userError);
        // Continue even if user creation fails
      }
    }
    
    // Set cookie for server-side auth check
    // Use domain-less cookie so it works for all subpaths
    if (data.session?.access_token) {
      try {
        // Set cookie that expires when session expires
        // Don't set domain so it works for all subpaths (ryansigler.com and subpaths)
        const expires = new Date(data.session.expires_at * 1000).toUTCString();
        const cookieValue = `sb-access-token=${data.session.access_token}; expires=${expires}; path=/; SameSite=Lax; Secure`;
        document.cookie = cookieValue;
        console.log('[AUTH] Auth cookie set successfully for path:', window.location.pathname);
      } catch (cookieError) {
        console.error('[AUTH] Error setting cookie:', cookieError);
        // Don't fail login if cookie setting fails
      }
    } else {
      console.warn('[AUTH] No session access token in login response');
    }
    
    return data;
  } catch (error) {
    console.error('[AUTH] Login failed:', error);
    throw error;
  }
}

/**
 * Sign up with email and password
 * @param {string} email
 * @param {string} password
 * @param {string} name - Optional display name
 * @returns {Promise<Object>}
 */
export async function signup(email, password, name) {
  // Sign up with Supabase
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        name: name || email.split('@')[0],
        display_name: name || email.split('@')[0]
      }
    }
  });
  
  if (error) throw error;
  
  // Create user in users table if signup successful
  if (data.user) {
    try {
      await getOrCreateUser(data.user.id);
      
      // Optionally create user in token service backend
      // This can be done via API call if needed
      try {
        const response = await fetch('https://diet-project-v1.onrender.com/api/auth/signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            name: name || email.split('@')[0]
          })
        });
        
        if (!response.ok) {
          console.warn('Failed to create user in token service, but Supabase user created');
        }
      } catch (apiError) {
        console.warn('Error creating user in token service:', apiError);
        // Don't fail signup if token service call fails
      }
    } catch (userError) {
      console.error('Error creating user record:', userError);
      // User is still created in Supabase Auth, just not in users table
    }
  }
  
  // Set cookie for server-side auth check (if session exists)
  if (data.session?.access_token) {
    const expires = new Date(data.session.expires_at * 1000).toUTCString();
    document.cookie = `sb-access-token=${data.session.access_token}; expires=${expires}; path=/; SameSite=Lax; Secure`;
  }
  
  return data;
}

/**
 * Logout
 * @returns {Promise<void>}
 */
export async function logout() {
  console.log('[AUTH] Logging out...');
  
  try {
    // Sign out from Supabase (this should clear the session)
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('[AUTH] Error during signOut:', error);
      throw error;
    }
    
    console.log('[AUTH] Supabase signOut successful');
    
    // Clear auth cookie
    document.cookie = 'sb-access-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'sb-access-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname + ';';
    console.log('[AUTH] Auth cookie cleared');
    
    // Clear Supabase session storage (Supabase stores session in localStorage)
    // Clear all Supabase-related localStorage items
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') || key.includes('supabase')) {
        localStorage.removeItem(key);
        console.log('[AUTH] Cleared localStorage:', key);
      }
    });
    
    // Clear any app-specific storage
    localStorage.removeItem('selectedProfile');
    
    // Force a small delay to ensure state updates
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Verify session is cleared
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      console.warn('[AUTH] Session still exists after logout, forcing clear');
      // Force clear by removing all auth-related storage
      localStorage.clear();
      sessionStorage.clear();
    } else {
      console.log('[AUTH] Session successfully cleared');
    }
  } catch (error) {
    console.error('[AUTH] Logout error:', error);
    // Even if signOut fails, try to clear local storage
    document.cookie = 'sb-access-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') || key.includes('supabase')) {
        localStorage.removeItem(key);
      }
    });
    throw error;
  }
}

/**
 * Listen for auth state changes
 * @param {Function} callback - Callback function (event, session) => {}
 * @returns {Function} Unsubscribe function
 */
export function onAuthStateChange(callback) {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
    // Update cookie when auth state changes
    if (session?.access_token) {
      const expires = new Date(session.expires_at * 1000).toUTCString();
      document.cookie = `sb-access-token=${session.access_token}; expires=${expires}; path=/; SameSite=Lax; Secure`;
    } else {
      // Clear cookie on logout
      document.cookie = 'sb-access-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
    
    // Call original callback
    callback(event, session);
  });
  return () => subscription.unsubscribe();
}

/**
 * Sign in with OAuth provider (Google, GitHub, etc.)
 * @param {string} provider - OAuth provider name
 * @param {string} redirectTo - URL to redirect after OAuth
 * @returns {Promise<void>}
 */
export async function signInWithOAuth(provider, redirectTo = window.location.origin) {
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${redirectTo}/auth/callback`
    }
  });
  
  if (error) throw error;
}

/**
 * Get session
 * @returns {Promise<Object|null>}
 */
export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

/**
 * Reset password - sends password reset email
 * @param {string} email
 * @returns {Promise<void>}
 */
export async function resetPassword(email) {
  // Use homepage URL explicitly to ensure redirect goes to homepage, not diet app
  const homepageUrl = process.env.REACT_APP_HOMEPAGE_URL || 'https://ryansigler.com';
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${homepageUrl}/?page=reset-password`
  });
  
  if (error) throw error;
}

/**
 * Update password - used after user clicks reset link in email
 * @param {string} newPassword
 * @returns {Promise<void>}
 */
export async function updatePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({
    password: newPassword
  });
  
  if (error) throw error;
}

/**
 * Force clear all auth state (for debugging/fixing stuck sessions)
 * @returns {Promise<void>}
 */
export async function forceClearAuth() {
  console.log('[AUTH] Force clearing all auth state...');
  
  try {
    // Sign out from Supabase
    await supabase.auth.signOut();
  } catch (error) {
    console.warn('[AUTH] Error during force signOut (continuing anyway):', error);
  }
  
  // Clear all cookies
  document.cookie.split(";").forEach((c) => {
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });
  
  // Clear all localStorage
  localStorage.clear();
  
  // Clear all sessionStorage
  sessionStorage.clear();
  
  console.log('[AUTH] All auth state cleared');
}
